import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  CBadge,
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CDropdown,
  CDropdownMenu,
  CDropdownToggle,
  CFormCheck,
  CFormLabel,
  CFormInput,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CTab,
  CTabContent,
  CTabList,
  CTabPanel,
  CTabs,
  CRow,
  CWidgetStatsB,
  CContainer,
} from "@coreui/react";
import { CIcon } from "@coreui/icons-react";
import { cilCheck, cilEnvelopeClosed, cilInfo, cilTrash } from "@coreui/icons";
import { jwtDecode } from "jwt-decode";
import { apiClient } from "services/Api";
import SearchSelect from "components/forms/SearchSelect";
import LoadingScreen from "components/globals/LoadingScreen";
import ErrorPage from "components/globals/ErrorPage";
import pasTeamworkImage from "assets/images/surveillance-guide/pas-teamwork.png";
import pasPlatformImage from "assets/images/surveillance-guide/pas-platform.png";
import sigaTeamworkImage from "assets/images/surveillance-guide/siga-teamwork.png";
import sigaPlatformImage from "assets/images/surveillance-guide/siga-platform.png";
import iapmeiTeamworkImage from "assets/images/surveillance-guide/iapmei-teamwork.png";
import iapmeiPlatformImage from "assets/images/surveillance-guide/iapmei-platform.png";
import sgoTeamworkImage from "assets/images/surveillance-guide/sgo-teamwork.png";
import sgoPlatformImage from "assets/images/surveillance-guide/sgo-platform.png";

const PAGE_SIZE = 6;
const RUN_STATUS_POLL_INTERVAL_MS = 60000;
const MANUAL_RUN_PLATFORM_OPTIONS = [
  { label: "PAS", value: "PAS" },
  { label: "IAPMEI", value: "IAPMEI" },
  { label: "SGO", value: "SGO" },
  { label: "SIGA", value: "SIGA" },
];

const platIcons = {
  SGO: "https://pas.compete2020.gov.pt/pas3/images/favicon.ico",
  // PAS: "https://pas.compete2020.gov.pt/pas2/consola2020/image/pas25.png",
  PAS: "https://pas.compete2020.gov.pt/favicon.ico",
  IAPMEI: "https://webapps.iapmei.pt/ConsolaPRR/assets/img/prr_symbol.png",
  SIGA: "https://benef.recuperarportugal.gov.pt/siga-bf/favicon.ico",
};

// Ordem canónica dos campos por plataforma. Chaves não listadas
// vão para o fim, por ordem alfabética (preserva compat. com novos
// campos adicionados no backend).
const FIELD_ORDER = {
  SGO: [
    "estado",
    "pedido_esclarecimento",
    "proposta_decisao",
    "decisao_final",
    "termo_aceitacao",
    "pedido_alteracao",
    "pedido_pagamento",
  ],
};

const FIELD_LABELS = {
  // SGO
  estado: "Estado",
  pedido_esclarecimento: "Pedido de esclarecimento",
  proposta_decisao: "Proposta de decisão",
  decisao_final: "Decisão final",
  termo_aceitacao: "Termo de aceitação",
  pedido_alteracao: "Pedido de alteração",
  pedido_pagamento: "Pedidos de pagamento",
  // SIGA
  ultima_notificacao: "Última notificação",
  ultimo_pedido: "Último pedido",
  pedidos_pagamento: "Pedidos de pagamento",
  // PAS
  situacao: "Situação",
  resumo: "Resumo",
  ultima_mensagem: "Última mensagem",
  // IAPMEI
  duplo_financiamento: "Duplo financiamento",
  ultimo_pagamento_recebido: "Último pagamento recebido",
  // genérico
  erro: "Erro",
};

const sortFieldsByPlatform = (fields, platform) => {
  const order = FIELD_ORDER[platform] || [];
  if (!order.length) return fields;
  return [...fields].sort((a, b) => {
    const ia = order.indexOf(a.key);
    const ib = order.indexOf(b.key);
    if (ia === -1 && ib === -1) return a.key.localeCompare(b.key);
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
};

const SURVEILLANCE_GUIDE = [
  {
    platform: "SGO",
    tag: "Acesso SGO",
    description:
      "A SABRINA encontra o projeto pelo Nº Projeto no nome do link ou pelo NUP no corpo do link.",
    matchLabel: "Na plataforma o NUP aparece como Código de operação.",
    extracts: [
      "Estado do projeto",
      "Decisão final",
      "Termo de aceitação",
      "Pedido de alteração",
      "Todos os pedidos de pagamento",
      "Eventual pedido de esclarecimento",
      "Proposta de decisão",
    ],
    teamworkImage: sgoTeamworkImage,
    platformImage: sgoPlatformImage,
  },
  {
    platform: "PAS",
    tag: "Acesso PAS",
    description:
      "A SABRINA encontra o projeto pelo Número do Projeto e pelo NIF no nome do link.",
    matchLabel:
      "O nome do link deve manter estes dois identificadores visíveis.",
    extracts: [
      "Resumo do projeto",
      "Situação atual",
      "Última mensagem",
      "Estado dos 2 últimos pedidos de pagamento",
    ],
    teamworkImage: pasTeamworkImage,
    platformImage: pasPlatformImage,
  },
  {
    platform: "SIGA",
    tag: "Acesso SIGA",
    description:
      "A SABRINA encontra o projeto pelo Número do Projeto no nome do link ou pelo NUP no corpo do link.",
    matchLabel:
      "Na plataforma o NUP aparece no campo T e pode existir antes da numeração final, após submissão.",
    extracts: [
      "Última notificação",
      "Último pedido",
      "Estado dos 2 últimos pedidos de pagamento",
    ],
    teamworkImage: sigaTeamworkImage,
    platformImage: sigaPlatformImage,
  },
  {
    platform: "IAPMEI",
    tag: "Acesso IAPMEI",
    description:
      "A SABRINA encontra o projeto através do Número do Projeto no nome do link.",
    matchLabel:
      "A correspondência é feita pela numeração do projeto mostrada na plataforma.",
    extracts: [
      "2 últimos pedidos de pagamento",
      "Duplo financiamento",
      "Último pagamento recebido",
    ],
    teamworkImage: iapmeiTeamworkImage,
    platformImage: iapmeiPlatformImage,
  },
];

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("pt-PT");
};

const formatDateTimeToMinute = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("pt-PT", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatMinutesDiff = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  const diffMs = Date.now() - date.getTime();
  const totalMinutes = Math.max(0, Math.floor(diffMs / 60000));
  if (totalMinutes < 60) return `Há ${totalMinutes} min`;
  if (totalMinutes > 1440) {
    const days = Math.floor(totalMinutes / 1440);
    return `Há ${days} dia${days > 1 ? "s" : ""}`;
  }
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return `Há ${hours}h ${minutes}m`;
};

const formatValue = (value) => {
  if (value === null || value === undefined) return "-";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value);
  } catch (err) {
    return String(value);
  }
};

const truncateValue = (value, max = 120) => {
  const normalized = formatValue(value);
  if (normalized.length <= max) return normalized;
  return `${normalized.slice(0, max)}...`;
};

const toPaymentObjects = (val) =>
  (Array.isArray(val) ? val : []).filter(
    (item) => item !== null && typeof item === "object" && !Array.isArray(item),
  );

const PAYMENT_COLLAPSED_MAX = 3;

const PaymentTable = ({ items, oldItems, newItems, className }) => {
  const [expanded, setExpanded] = useState(false);
  const isDiff = oldItems !== undefined || newItems !== undefined;
  const safeItems = toPaymentObjects(items || []);
  const safeOld = toPaymentObjects(oldItems || []);
  const safeNew = toPaymentObjects(newItems || []);

  const totalRows = isDiff
    ? Math.max(safeOld.length, safeNew.length)
    : safeItems.length;

  if (!totalRows) return <span className={className || ""}>-</span>;

  const showToggle = totalRows > PAYMENT_COLLAPSED_MAX;
  const startIndex =
    showToggle && !expanded ? Math.max(0, totalRows - PAYMENT_COLLAPSED_MAX) : 0;
  const hiddenCount = totalRows - PAYMENT_COLLAPSED_MAX;

  const all = isDiff ? [...safeOld, ...safeNew] : safeItems;
  const hasElementos = all.some((item) => item?.pedido_elementos);
  const hasDecisao = all.some((item) => item?.decisao_pedido);

  const cellDiff = (oldVal, newVal) => {
    if (!oldVal && !newVal) return "-";
    if (oldVal === newVal) return truncateValue(oldVal, 120);
    return (
      <>
        {oldVal ? (
          <span className="surveillance-cell-old">{truncateValue(oldVal, 120)}</span>
        ) : null}
        {oldVal && newVal ? (
          <span className="surveillance-cell-arrow"> → </span>
        ) : null}
        {newVal ? (
          <span className="surveillance-cell-new">{truncateValue(newVal, 120)}</span>
        ) : null}
      </>
    );
  };

  return (
    <div className={className || ""}>
      {showToggle && !expanded ? (
        <button
          type="button"
          className="surveillance-payment-toggle"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(true);
          }}
        >
          +{hiddenCount} anteriores
        </button>
      ) : null}
      <table className="surveillance-payment-table">
        <thead>
          <tr>
            <th>Pedido</th>
            {hasElementos ? <th>Elementos</th> : null}
            {hasDecisao ? <th>Decisão</th> : null}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: totalRows - startIndex }, (_, i) => {
            const idx = startIndex + i;
            if (isDiff) {
              const o = safeOld[idx] || {};
              const n = safeNew[idx] || {};
              return (
                <tr key={idx}>
                  <td>{cellDiff(o.pedido_pagamento, n.pedido_pagamento)}</td>
                  {hasElementos ? (
                    <td>{cellDiff(o.pedido_elementos, n.pedido_elementos)}</td>
                  ) : null}
                  {hasDecisao ? (
                    <td>{cellDiff(o.decisao_pedido, n.decisao_pedido)}</td>
                  ) : null}
                </tr>
              );
            }
            const item = safeItems[idx];
            return (
              <tr key={idx}>
                <td>{truncateValue(item?.pedido_pagamento, 180)}</td>
                {hasElementos ? (
                  <td>
                    {item?.pedido_elementos
                      ? truncateValue(item.pedido_elementos, 180)
                      : "-"}
                  </td>
                ) : null}
                {hasDecisao ? (
                  <td>
                    {item?.decisao_pedido
                      ? truncateValue(item.decisao_pedido, 180)
                      : "-"}
                  </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </table>
      {showToggle && expanded ? (
        <button
          type="button"
          className="surveillance-payment-toggle"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(false);
          }}
        >
          Mostrar menos
        </button>
      ) : null}
    </div>
  );
};

const formatStatusLabel = (value) => {
  if (value === "new") return "Projetos novos";
  if (value === "updated") return "Novidades";
  if (value === "auth_error") return "Erro de autenticação";
  if (value === "surveillance_error") return "Erro de vigilância";
  return value;
};

const getProjectChanges = (project) => {
  if (!project.history || !Object.keys(project.history).length) return [];

  const current = project?.surveillance || {};
  const previous = project?.history || {};
  const changes = [];
  const keys = new Set([
    ...Object.keys(current || {}),
    ...Object.keys(previous || {}),
  ]);
  keys.forEach((key) => {
    const oldValue = previous?.[key];
    const newValue = current?.[key];
    if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
      changes.push({
        key,
        oldValue,
        newValue,
      });
    }
  });
  return changes;
};

const getProjectFields = (project, changes) => {
  const current = project?.surveillance || {};
  const previous = project?.history || {};
  const changeMap = new Map(changes.map((change) => [change.key, change]));
  const keys = new Set([
    ...Object.keys(current || {}),
    ...Object.keys(previous || {}),
  ]);
  const fields = Array.from(keys).map((key) => {
    const change = changeMap.get(key);
    return {
      key,
      value: current?.[key],
      oldValue: change?.oldValue,
      changed: Boolean(change),
    };
  });
  return sortFieldsByPlatform(fields, project?.platform);
};

const Surveillance = () => {
  const [clients, setClients] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [runMeta, setRunMeta] = useState(null);
  const [runLoading, setRunLoading] = useState(false);
  const [runFeedback, setRunFeedback] = useState("");
  const [runPlatforms, setRunPlatforms] = useState([]);
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [guideTab, setGuideTab] = useState(SURVEILLANCE_GUIDE[0].platform);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [deletingProjectId, setDeletingProjectId] = useState(null);
  const [notificationsMeta, setNotificationsMeta] = useState({
    page: 1,
    pageSize: PAGE_SIZE,
    total: 0,
    unread: 0,
  });
  const [filters, setFilters] = useState({
    client: "",
    platform: [],
    typology: [],
    status: [],
  });
  const [mailboxOpen, setMailboxOpen] = useState(false);
  const [runDeleting, setRunDeleting] = useState(false);
  const lastRunRef = useRef(null);

  const isSuperuser = useMemo(() => {
    const token = localStorage.getItem("access_token");
    if (!token) return false;
    try {
      return !!jwtDecode(token)?.is_superuser;
    } catch (err) {
      return false;
    }
  }, []);

  const applyNotificationsPayload = useCallback((payload, fallbackPage = 1) => {
    if (Array.isArray(payload)) {
      const unread = payload.filter((item) => !item.seen).length;
      setNotifications(payload);
      setNotificationsMeta({
        page: 1,
        pageSize: PAGE_SIZE,
        total: payload.length,
        unread,
      });
      return;
    }

    const results = payload?.results || [];
    setNotifications(results);
    setNotificationsMeta({
      page: payload?.page || fallbackPage,
      pageSize: payload?.page_size || PAGE_SIZE,
      total: payload?.count ?? results.length,
      unread:
        payload?.unread_count ?? results.filter((item) => !item.seen).length,
    });
  }, []);

  const loadNotifications = (page = 1) => {
    setNotificationsLoading(true);
    apiClient
      .get(`/projects/notifications/?page=${page}&page_size=${PAGE_SIZE}`)
      .then((response) => {
        applyNotificationsPayload(response.data || [], page);
      })
      .catch(() => {
        setError(true);
      })
      .finally(() => {
        setNotificationsLoading(false);
      });
  };

  const refreshSurveillanceData = useCallback(
    (page = 1) => {
      return Promise.all([
        apiClient.get("/projects/surveillance/"),
        apiClient.get(
          `/projects/notifications/?page=${page}&page_size=${PAGE_SIZE}`,
        ),
      ]).then(([clientsResponse, notificationsResponse]) => {
        setClients(clientsResponse.data || []);
        applyNotificationsPayload(notificationsResponse.data || [], page);
      });
    },
    [applyNotificationsPayload],
  );

  useEffect(() => {
    setLoading(true);
    Promise.all([
      refreshSurveillanceData(1),
      apiClient.get("/projects/surveillance/run/"),
    ])
      .then(([, runResponse]) => {
        const run = runResponse.data?.id ? runResponse.data : null;
        lastRunRef.current = run;
        setRunMeta(run);
      })
      .catch(() => {
        setError(true);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [refreshSurveillanceData]);

  const syncRunStatus = useCallback(() => {
    return apiClient
      .get("/projects/surveillance/run/")
      .then((response) => {
        const nextRun = response.data?.id ? response.data : null;
        const prevRun = lastRunRef.current;

        const finishedNow =
          prevRun?.id &&
          prevRun?.status === "running" &&
          nextRun?.id === prevRun.id &&
          nextRun?.status &&
          nextRun.status !== "running";
        const completedDifferentRun =
          prevRun?.id &&
          nextRun?.id &&
          prevRun.id !== nextRun.id &&
          nextRun?.status &&
          nextRun.status !== "running";

        lastRunRef.current = nextRun;
        setRunMeta(nextRun);

        if (finishedNow || completedDifferentRun) {
          const currentPage = notificationsMeta.page || 1;
          refreshSurveillanceData(currentPage).catch(() => setError(true));
        }
      })
      .catch(() => {
        // Non-blocking poll: keep existing page state on transient errors.
      });
  }, [notificationsMeta.page, refreshSurveillanceData]);

  useEffect(() => {
    if (runMeta?.status !== "running") {
      return undefined;
    }

    const intervalId = setInterval(() => {
      syncRunStatus();
    }, RUN_STATUS_POLL_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [runMeta?.status, syncRunStatus]);

  const triggerManualRun = (platforms = []) => {
    if (runMeta?.status === "running") {
      setRunFeedback("Já existe uma execução de vigilância em curso.");
      return;
    }

    setRunLoading(true);
    setRunFeedback("");
    apiClient
      .post("/projects/surveillance/run/", {
        platforms,
      })
      .then((response) => {
        setRunFeedback(
          response.data?.message || "Execução de vigilância agendada.",
        );
        window.location.reload();
      })
      .catch((error) => {
        setRunFeedback(
          error?.response?.data?.message ||
            "Erro ao agendar execução de vigilância.",
        );
      })
      .finally(() => {
        setRunLoading(false);
      });
  };

  const handleDeleteRun = () => {
    if (!runMeta?.id) return;

    const confirmed = window.confirm(
      "Tem a certeza que quer eliminar esta execução de vigilância?",
    );
    if (!confirmed) return;

    setRunDeleting(true);
    apiClient
      .delete(`/projects/surveillance/run/${runMeta.id}/`)
      .then(() => {
        lastRunRef.current = null;
        setRunMeta(null);
        setRunFeedback("Execução de vigilância eliminada.");
      })
      .catch((error) => {
        setRunFeedback(
          error?.response?.data?.detail ||
            error?.response?.data?.error ||
            "Erro ao eliminar execução de vigilância.",
        );
      })
      .finally(() => {
        setRunDeleting(false);
      });
  };

  const runSummary = useMemo(() => {
    if (!runMeta?.id) return "Sem execuções registadas.";

    let statusLabel = "Em execução";
    if (runMeta.status === "success") {
      statusLabel = "Concluída";
    } else if (runMeta.status === "error") {
      statusLabel = "Com erro";
    }

    const started = formatDateTime(runMeta.started_at);
    const ended = formatDateTime(runMeta.ended_at);
    const platforms = Array.isArray(runMeta.selected_platforms)
      ? runMeta.selected_platforms
      : [];
    const platformLabel = platforms.length
      ? ` | Plataformas: ${platforms.join(", ")}`
      : "";
    return `Última execução: ${statusLabel} | Início: ${started} | Fim: ${ended}${platformLabel}`;
  }, [runMeta]);

  const platformOptions = useMemo(() => {
    const set = new Set();
    clients.forEach((client) => {
      client.projects.forEach((project) => {
        if (project.platform) set.add(project.platform);
      });
    });
    return Array.from(set).sort();
  }, [clients]);

  const typologyOptions = useMemo(() => {
    const set = new Set();
    clients.forEach((client) => {
      client.projects.forEach((project) => {
        if (project.typology) set.add(project.typology);
      });
    });
    return Array.from(set).sort();
  }, [clients]);

  const projectStatus = (project) => {
    if (project.error_type === "Autenticação") return "auth_error";
    if (project.error_type === "Vigilância") return "surveillance_error";
    if (project.history && !Object.keys(project.history).length) return "new";
    if (getProjectChanges(project).length) return "updated";
    return "ok";
  };

  const filteredClients = useMemo(() => {
    if (!clients.length) return [];

    return clients
      .map((client) => {
        const projects = client.projects.filter((project) => {
          const clientMatches = filters.client
            ? client.name.toLowerCase().includes(filters.client.toLowerCase())
            : true;
          const platformMatches = filters.platform.length
            ? filters.platform.includes(project.platform)
            : true;
          const typologyMatches = filters.typology.length
            ? filters.typology.includes(project.typology)
            : true;
          const statusMatches = filters.status.length
            ? filters.status.includes(projectStatus(project))
            : true;

          return (
            clientMatches && platformMatches && typologyMatches && statusMatches
          );
        });
        return { ...client, projects };
      })
      .filter((client) => client.projects.length > 0);
  }, [clients, filters]);

  const projectsFlat = useMemo(
    () => filteredClients.flatMap((client) => client.projects),
    [filteredClients],
  );

  const projectChanges = useMemo(() => {
    const map = new Map();
    projectsFlat.forEach((project) => {
      map.set(project.id, getProjectChanges(project));
    });
    return map;
  }, [projectsFlat]);

  const lastUpdate = useMemo(() => {
    const max = projectsFlat.reduce((acc, project) => {
      const value = new Date(project.updated_at).getTime();
      return value > acc ? value : acc;
    }, 0);
    return max ? new Date(max) : null;
  }, [projectsFlat]);

  const errorSummary = useMemo(() => {
    let auth = 0;
    let surveillance = 0;
    projectsFlat.forEach((project) => {
      if (project.error_type === "Autenticação") auth += 1;
      if (project.error_type === "Vigilância") surveillance += 1;
    });
    return { auth, surveillance, total: auth + surveillance };
  }, [projectsFlat]);

  const newsCount = useMemo(() => {
    return projectsFlat.filter(
      (project) => (projectChanges.get(project.id) || []).length > 0,
    ).length;
  }, [projectsFlat, projectChanges]);

  const totalPages = Math.max(
    1,
    Math.ceil(notificationsMeta.total / notificationsMeta.pageSize),
  );

  const toggleRunPlatform = (platform) => {
    setRunPlatforms((prev) =>
      prev.includes(platform)
        ? prev.filter((item) => item !== platform)
        : [...prev, platform],
    );
  };

  const isObjectArray = (val) => toPaymentObjects(val).length > 0;

  const renderFieldValue = (value, className) => {
    if (Array.isArray(value)) {
      if (!value.length) return <span className={className}>-</span>;
      if (toPaymentObjects(value).length > 0) {
        return <PaymentTable items={value} className={className} />;
      }
      return (
        <ul className={`surveillance-field-list ${className || ""}`}>
          {value.map((item, index) => (
            <li key={`${className || "value"}-${index}`}>
              {truncateValue(item, 180)}
            </li>
          ))}
        </ul>
      );
    }
    return <span className={className}>{truncateValue(value)}</span>;
  };

  const handleMarkRead = (id) => {
    apiClient
      .put(`/projects/notifications/${id}/`)
      .then(() => loadNotifications(notificationsMeta.page))
      .catch(() => setError(true));
  };

  const handleDelete = (id) => {
    apiClient
      .delete(`/projects/notifications/${id}/`)
      .then(() => loadNotifications(notificationsMeta.page))
      .catch(() => setError(true));
  };

  const handleProjectDelete = (project, clientName) => {
    const projectLabel = project.number
      ? `o projeto ${project.number}`
      : "este registo";
    const confirmed = window.confirm(
      `Tem a certeza que quer eliminar ${projectLabel} de ${clientName}?`,
    );

    if (!confirmed) return;

    setDeletingProjectId(project.id);
    apiClient
      .delete(`/projects/surveillance/${project.id}/`)
      .then(() => {
        const currentPage = notificationsMeta.page || 1;
        return refreshSurveillanceData(currentPage);
      })
      .catch(() => setError(true))
      .finally(() => {
        setDeletingProjectId(null);
      });
  };

  if (error) return <ErrorPage message="Erro ao carregar vigilância." />;
  if (loading) return <LoadingScreen message="A carregar vigilância..." />;

  return (
    <CContainer>
      <CCardBody>
        <div className="surveillance-runbar mb-4">
          <div className="surveillance-runbar-copy">
            <div className="surveillance-runbar-title">
              Vigilância de projetos
            </div>
            <div className="surveillance-runbar-summary">{runSummary}</div>
            <div className="surveillance-runbar-meta">
              <CBadge color="light" className="surveillance-runbar-badge">
                {runMeta?.status === "running" ? "Em execução" : "Disponível"}
              </CBadge>
              <CBadge color="light" className="surveillance-runbar-badge">
                {projectsFlat.length} projetos visíveis
              </CBadge>
              <CBadge color="light" className="surveillance-runbar-badge">
                {notificationsMeta.unread} notificações por ler
              </CBadge>
            </div>
          </div>
          <div className="surveillance-runbar-actions">
            <CButton
              color="info"
              size="sm"
              className="me-2"
              // className="surveillance-guide-button"
              onClick={() => setInfoModalOpen(true)}
            >
              <CIcon icon={cilInfo} />
            </CButton>
            {isSuperuser && runMeta?.status === "running" ? (
              <CButton
                color="danger"
                variant="outline"
                size="sm"
                className="me-2"
                disabled={runDeleting}
                title="Eliminar execução presa (worker terminado a meio)"
                onClick={handleDeleteRun}
              >
                {runDeleting ? "A eliminar..." : "Eliminar execução"}
              </CButton>
            ) : null}
            <CButton
              color="primary"
              size="sm"
              className="surveillance-runbar-button"
              disabled={runLoading || runMeta?.status === "running"}
              onClick={() => triggerManualRun([])}
            >
              {runLoading
                ? "A agendar..."
                : runMeta?.status === "running"
                  ? "Vigilância em execução"
                  : "Executar vigilância"}
            </CButton>
            <CDropdown alignment="end" autoClose="outside">
              <CDropdownToggle
                color="primary"
                variant="outline"
                split
                size="sm"
                disabled={runLoading || runMeta?.status === "running"}
              />
              <CDropdownMenu className="surveillance-runbar-menu p-3 shadow-sm">
                <div className="surveillance-runbar-menu-title">
                  Filtrar plataformas
                </div>
                <div className="surveillance-runbar-menu-subtitle">
                  O botão principal executa sempre todas. Use este menu para uma
                  execução filtrada.
                </div>
                <div className="surveillance-runbar-menu-options">
                  {MANUAL_RUN_PLATFORM_OPTIONS.map((option) => (
                    <CFormCheck
                      key={option.value}
                      id={`run-platform-${option.value}`}
                      className="surveillance-runbar-check"
                      label={option.label}
                      checked={runPlatforms.includes(option.value)}
                      onChange={() => toggleRunPlatform(option.value)}
                    />
                  ))}
                </div>
                <div className="surveillance-runbar-menu-actions">
                  <button
                    type="button"
                    className="surveillance-runbar-menu-link btn btn-link p-0"
                    onClick={() => setRunPlatforms([])}
                  >
                    Limpar seleção
                  </button>
                  <CButton
                    color="primary"
                    size="sm"
                    disabled={!runPlatforms.length}
                    onClick={() => triggerManualRun(runPlatforms)}
                  >
                    Executar filtrada
                  </CButton>
                </div>
              </CDropdownMenu>
            </CDropdown>
          </div>
        </div>
        <div>
          {runFeedback ? (
            <div className="text-medium-emphasis small mb-3">{runFeedback}</div>
          ) : null}
        </div>
        <CModal
          visible={infoModalOpen}
          onClose={() => setInfoModalOpen(false)}
          alignment="center"
          size="xl"
          className="surveillance-guide-modal"
        >
          <CModalHeader>
            <CModalTitle>Guia de configuração da SABRINA</CModalTitle>
          </CModalHeader>
          <CModalBody>
            <div className="surveillance-guide-intro">
              Configure corretamente os links de acesso na Teamwork para a
              SABRINA conseguir localizar os projetos e extrair a informação de
              vigilância certa.
            </div>

            <CCard className="surveillance-guide-rules-card">
              <CCardBody>
                <div className="surveillance-guide-section-title">
                  Regras gerais
                </div>
                <ul className="surveillance-guide-list mb-0">
                  <li>Cada acesso deve estar num link da Teamwork.</li>
                  <li>A tag da plataforma tem de estar no link correto.</li>
                  <li>
                    O nome do link e o corpo devem conter os identificadores
                    esperados para a plataforma.
                  </li>
                  <li>
                    A tag <strong>Super-Utilizador</strong> não é necessária
                    para a vigilância.
                  </li>
                  <li>
                    A SABRINA aplica a tag <strong>Password Errada</strong> ao
                    link assim que deteta credenciais inválidas.
                  </li>
                  <li>
                    Enquanto a tag <strong>Password Errada</strong> não for
                    removida manualmente, o projeto é ignorado para evitar
                    bloquear o acesso da plataforma.
                  </li>
                </ul>
              </CCardBody>
            </CCard>

            <CTabs activeItemKey={guideTab} onChange={setGuideTab}>
              <CTabList variant="tabs" className="surveillance-guide-tabs">
                {SURVEILLANCE_GUIDE.map((item) => (
                  <CTab itemKey={item.platform} key={item.platform}>
                    {item.platform}
                  </CTab>
                ))}
              </CTabList>
              <CTabContent>
                {SURVEILLANCE_GUIDE.map((item) => (
                  <CTabPanel itemKey={item.platform} key={item.platform}>
                    <CCard className="surveillance-guide-platform-card">
                      <CCardBody>
                        <div className="surveillance-guide-platform-head">
                          <div className="surveillance-guide-platform-title">
                            {item.platform}
                          </div>
                          <CBadge
                            color="info"
                            className="surveillance-guide-tag"
                          >
                            {item.tag}
                          </CBadge>
                        </div>

                        <div className="surveillance-guide-section-title">
                          Configuração
                        </div>

                        <div className="surveillance-guide-copy">
                          {item.description}
                        </div>
                        <div className="surveillance-guide-note">
                          {item.matchLabel}
                        </div>

                        <div className="surveillance-guide-images is-stacked">
                          <div className="surveillance-guide-image-card">
                            <div className="surveillance-guide-image-label">
                              Exemplo Teamwork
                            </div>
                            <img
                              src={item.teamworkImage}
                              alt={`${item.platform} Teamwork`}
                              className="surveillance-guide-image"
                            />
                          </div>
                          <div className="surveillance-guide-image-card">
                            <div className="surveillance-guide-image-label">
                              Exemplo Plataforma
                            </div>
                            <img
                              src={item.platformImage}
                              alt={`${item.platform} plataforma`}
                              className="surveillance-guide-image"
                            />
                          </div>
                        </div>

                        <div className="surveillance-guide-section-title">
                          Informação extraída
                        </div>
                        <ul className="surveillance-guide-list">
                          {item.extracts.map((extract) => (
                            <li key={`${item.platform}-${extract}`}>
                              {extract}
                            </li>
                          ))}
                        </ul>
                      </CCardBody>
                    </CCard>
                  </CTabPanel>
                ))}
              </CTabContent>
            </CTabs>
          </CModalBody>
        </CModal>
        <CRow className="g-3 mb-4 align-items-stretch">
          <CCol sm={6} lg={3}>
            <CWidgetStatsB
              className="surveillance-kpi h-100 kpi-total"
              value={lastUpdate ? formatMinutesDiff(lastUpdate) : "-"}
              title="Ultima atualização"
              text="Baseado nos filtros ativos."
            />
          </CCol>
          <CCol sm={6} lg={3}>
            <CWidgetStatsB
              className="surveillance-kpi h-100 kpi-completion"
              value={projectsFlat.length}
              title="Total de projetos"
              text={`Clientes: ${filteredClients.length}`}
            />
          </CCol>
          <CCol sm={6} lg={3}>
            <CWidgetStatsB
              className="surveillance-kpi h-100 kpi-overdue"
              value={errorSummary.total}
              title="Erros de vigilância"
              text={`Autenticação: ${errorSummary.auth} • Vigilância: ${errorSummary.surveillance}`}
            />
          </CCol>
          <CCol sm={6} lg={3}>
            <CWidgetStatsB
              className="surveillance-kpi h-100 kpi-pending"
              value={newsCount}
              title="Novidades"
              text="Projetos com alterações."
            />
          </CCol>
        </CRow>

        <div className="management-filters mb-4">
          <div className="management-filters-header">
            <div className="management-section-title">Filtros</div>
            <CButton
              color="light"
              size="sm"
              onClick={() =>
                setFilters({
                  client: "",
                  platform: [],
                  typology: [],
                  status: [],
                })
              }
            >
              Limpar filtros
            </CButton>
          </div>
          <CRow className="g-3 management-filters-grid">
            <CCol xs={12} md={6} lg={4}>
              <CFormLabel>Empresa</CFormLabel>
              <CFormInput
                placeholder="Pesquisar por cliente"
                value={filters.client}
                onChange={(event) =>
                  setFilters((prev) => ({
                    ...prev,
                    client: event.target.value,
                  }))
                }
              />
            </CCol>
            <CCol xs={12} md={6} lg={3}>
              <SearchSelect
                label="Plataforma"
                isMulti
                options={platformOptions.map((option) => ({
                  label: option,
                  value: option,
                }))}
                value={filters.platform}
                onChange={(values) =>
                  setFilters((prev) => ({ ...prev, platform: values }))
                }
                placeholder="Selecione plataformas"
              />
            </CCol>
            <CCol xs={12} md={6} lg={3}>
              <SearchSelect
                label="Tipologia"
                isMulti
                options={typologyOptions.map((option) => ({
                  label: option,
                  value: option,
                }))}
                value={filters.typology}
                onChange={(values) =>
                  setFilters((prev) => ({ ...prev, typology: values }))
                }
                placeholder="Selecione tipologias"
              />
            </CCol>
            <CCol xs={12} md={6} lg={2}>
              <SearchSelect
                label="Estado"
                isMulti
                options={[
                  { label: "Projetos novos", value: "new" },
                  { label: "Novidades", value: "updated" },
                  { label: "Erro de autenticação", value: "auth_error" },
                  { label: "Erro de vigilância", value: "surveillance_error" },
                ]}
                value={filters.status}
                onChange={(values) =>
                  setFilters((prev) => ({ ...prev, status: values }))
                }
                placeholder="Selecione estados"
              />
            </CCol>
          </CRow>
          <div className="management-active-filters">
            {[
              filters.client && `Empresa: ${filters.client}`,
              filters.platform.length &&
                `Plataforma: ${filters.platform.join(", ")}`,
              filters.typology.length &&
                `Tipologia: ${filters.typology.join(", ")}`,
              filters.status.length &&
                `Estado: ${filters.status.map((status) => formatStatusLabel(status)).join(", ")}`,
            ]
              .filter(Boolean)
              .map((filter) => (
                <CBadge key={filter} color="info" className="projects-badge">
                  {filter}
                </CBadge>
              ))}
            {!filters.client &&
            !filters.platform.length &&
            !filters.typology.length &&
            !filters.status.length ? (
              <span className="text-medium-emphasis small">
                Sem filtros aplicados.
              </span>
            ) : null}
          </div>
        </div>

        <div className="surveillance-legend mb-4">
          <span className="surveillance-legend-title">Legenda</span>
          <CBadge className="surveillance-legend-badge legend-new">
            Projeto novo
          </CBadge>
          <CBadge className="surveillance-legend-badge legend-updated">
            Projeto com novidades
          </CBadge>
          <CBadge className="surveillance-legend-badge legend-error">
            Projeto com erro
          </CBadge>
        </div>

        {!filteredClients.length ? (
          <CCard className="surveillance-empty-card">
            <CCardBody>
              <div className="surveillance-empty-title">
                Sem projetos para apresentar
              </div>
              <div className="surveillance-empty-copy">
                Não existem projetos de vigilância com os filtros atuais.
              </div>
            </CCardBody>
          </CCard>
        ) : null}

        {filteredClients.map((client) => (
          <CCard key={client.name} className="surveillance-client-card">
            <CCardHeader className="surveillance-client-header">
              <div className="surveillance-client-info">
                {client.image ? (
                  <img
                    src={client.image}
                    alt={client.name}
                    className="surveillance-client-logo"
                  />
                ) : (
                  <div className="surveillance-client-logo fallback">
                    {client.name?.[0] || "?"}
                  </div>
                )}
                <div>
                  <div className="surveillance-client-name">{client.name}</div>
                  <div className="text-medium-emphasis small">
                    {client.projects.length} projetos
                  </div>
                </div>
              </div>
            </CCardHeader>
            <CCardBody>
              <div className="surveillance-project-grid">
                {client.projects.map((project) => {
                  const status = projectStatus(project);
                  const changes = projectChanges.get(project.id) || [];
                  const fields = getProjectFields(project, changes);
                  const platformIcon = platIcons[project.platform];
                  const isPAS = project.platform === "PAS";
                  return (
                    <div
                      key={project.id || project.number}
                      className={`surveillance-project-card surveillance-project--${status}`}
                      role="button"
                      tabIndex={0}
                      onClick={() =>
                        window.open(
                          `https://streamconsulting.teamwork.com/app/links/${project.id}`,
                          "_blank",
                        )
                      }
                      onKeyDown={(event) => {
                        if (event.target !== event.currentTarget) return;
                        if (event.key === "Enter") {
                          window.open(
                            `https://streamconsulting.teamwork.com/app/links/${project.id}`,
                            "_blank",
                          );
                        }
                      }}
                    >
                      <div className="surveillance-project-head">
                        <div>
                          <div className="surveillance-project-title">
                            Projeto {project.number || "-"}
                          </div>
                          <div className="surveillance-project-subtitle">
                            {project.typology || "Sem tipologia"}
                          </div>
                        </div>
                        <div className="surveillance-project-actions">
                          {platformIcon ? (
                            <img
                              src={platformIcon}
                              alt={project.platform}
                              className={
                                isPAS
                                  ? "surveillance-platform-pas-icon"
                                  : "surveillance-platform-icon"
                              }
                            />
                          ) : null}
                          <CButton
                            color="danger"
                            variant="ghost"
                            size="sm"
                            className="surveillance-project-action"
                            aria-label="Eliminar registo"
                            title="Eliminar registo"
                            disabled={deletingProjectId === project.id}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleProjectDelete(project, client.name);
                            }}
                          >
                            <CIcon icon={cilTrash} />
                          </CButton>
                        </div>
                      </div>
                      <div className="surveillance-project-meta">
                        <span>{client.name}</span>
                        <span>{project.platform || "Sem plataforma"}</span>
                      </div>
                      <div className="surveillance-project-tags">
                        {status === "new" ? (
                          <CBadge className="surveillance-tag tag-new">
                            Novo
                          </CBadge>
                        ) : null}
                        {status === "updated" ? (
                          <CBadge className="surveillance-tag tag-updated">
                            Novidades
                          </CBadge>
                        ) : null}
                        {status === "error" ? (
                          <CBadge className="surveillance-tag tag-error">
                            Erro
                          </CBadge>
                        ) : null}
                      </div>
                      {project.error_type ? (
                        <div className="surveillance-project-error">
                          <strong>{project.error_type}</strong>
                          <span>
                            {project.error_message || "Erro desconhecido"}
                          </span>
                        </div>
                      ) : null}
                      <div className="surveillance-project-fields">
                        {fields.map((field) => {
                          const label = FIELD_LABELS[field.key] ?? field.key;
                          if (
                            field.changed &&
                            (isObjectArray(field.value) || isObjectArray(field.oldValue))
                          ) {
                            return (
                              <div
                                key={`${project.id}-${field.key}`}
                                className="surveillance-field-row"
                              >
                                <span className="surveillance-field-key">
                                  {label}
                                </span>
                                <PaymentTable
                                  oldItems={field.oldValue}
                                  newItems={field.value}
                                />
                              </div>
                            );
                          }
                          if (field.changed) {
                            return (
                              <div
                                key={`${project.id}-${field.key}`}
                                className="surveillance-field-row is-changed"
                              >
                                <span className="surveillance-field-key">
                                  {label}
                                </span>
                                {renderFieldValue(
                                  field.oldValue,
                                  "surveillance-field-old",
                                )}
                                <span className="surveillance-field-arrow">
                                  →
                                </span>
                                {renderFieldValue(
                                  field.value,
                                  "surveillance-field-new",
                                )}
                              </div>
                            );
                          }
                          return (
                            <div
                              key={`${project.id}-${field.key}`}
                              className="surveillance-field-row"
                            >
                              <span className="surveillance-field-key">
                                {label}
                              </span>
                              {renderFieldValue(
                                field.value,
                                "surveillance-field-value",
                              )}
                            </div>
                          );
                        })}
                      </div>
                      <div className="surveillance-project-updated-at">
                        Atualizado em{" "}
                        {formatDateTimeToMinute(project.updated_at)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CCardBody>
          </CCard>
        ))}
        <div className="surveillance-mailbox">
          <CButton
            color="primary"
            className="surveillance-mailbox-toggle"
            onClick={() => setMailboxOpen((prev) => !prev)}
          >
            <CIcon
              icon={cilEnvelopeClosed}
              className="surveillance-mailbox-icon"
            />
            Notificações
            {notificationsMeta.unread ? (
              <CBadge color="danger" className="surveillance-mailbox-badge">
                {notificationsMeta.unread}
              </CBadge>
            ) : null}
          </CButton>
          <div
            className={`surveillance-mailbox-panel ${
              mailboxOpen ? "is-open" : ""
            }`}
          >
            <div className="surveillance-mailbox-header">
              <div>
                <div className="surveillance-section-title">Notificações</div>
                <div className="text-medium-emphasis small">
                  Por ler: {notificationsMeta.unread}
                </div>
              </div>
              <div className="surveillance-notifications-pages">
                <CButton
                  color="light"
                  size="sm"
                  disabled={notificationsMeta.page <= 1}
                  onClick={() => loadNotifications(notificationsMeta.page - 1)}
                >
                  Anterior
                </CButton>
                <span className="small">
                  {notificationsMeta.page} / {totalPages}
                </span>
                <CButton
                  color="light"
                  size="sm"
                  disabled={notificationsMeta.page >= totalPages}
                  onClick={() => loadNotifications(notificationsMeta.page + 1)}
                >
                  Seguinte
                </CButton>
              </div>
            </div>
            <div className="surveillance-mailbox-body">
              {notificationsLoading ? (
                <div className="text-center py-4">A carregar...</div>
              ) : null}
              {!notificationsLoading && !notifications.length ? (
                <div className="text-medium-emphasis small">
                  Sem notificações recentes.
                </div>
              ) : null}
              <div className="surveillance-notifications-list">
                {notifications.map((notification) => {
                  const client = {
                    name: notification.client_name,
                    image: notification.client_logo,
                  };
                  return (
                    <div
                      key={notification.id}
                      className={`surveillance-notification ${
                        !notification.seen ? "is-unread" : ""
                      }`}
                    >
                      <div className="surveillance-notification-header">
                        <div className="surveillance-notification-meta">
                          {client.image ? (
                            <img
                              src={client.image}
                              alt={client.name || "Cliente"}
                              className="surveillance-notification-logo"
                            />
                          ) : (
                            <div className="surveillance-notification-logo fallback">
                              {client.name?.[0] || "?"}
                            </div>
                          )}
                          <div>
                            <div className="surveillance-notification-title">
                              {notification.title}
                            </div>
                            <div className="surveillance-notification-client">
                              {client.name || "Cliente não identificado"}
                            </div>
                          </div>
                        </div>
                        <div className="surveillance-notification-actions">
                          {!notification.seen ? (
                            <CButton
                              color="primary"
                              variant="ghost"
                              size="sm"
                              className="surveillance-notification-action"
                              aria-label="Marcar como lida"
                              title="Marcar como lida"
                              onClick={() => handleMarkRead(notification.id)}
                            >
                              <CIcon icon={cilCheck} />
                            </CButton>
                          ) : null}
                          <CButton
                            color="danger"
                            variant="ghost"
                            size="sm"
                            className="surveillance-notification-action"
                            aria-label="Apagar"
                            title="Apagar"
                            onClick={() => handleDelete(notification.id)}
                          >
                            <CIcon icon={cilTrash} />
                          </CButton>
                        </div>
                      </div>
                      <div
                        className="surveillance-notification-content"
                        dangerouslySetInnerHTML={{
                          __html: notification.content,
                        }}
                      />
                      <div className="surveillance-notification-date">
                        {formatDateTime(notification.created_at)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </CCardBody>
    </CContainer>
  );
};

export default Surveillance;
